import "../../../../../assets/fa-icons/css/fontawesome.css";
import "../../../../../assets/fa-icons/css/sharp-solid.css";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { Zoom, Keyboard } from "swiper/modules";
import "swiper/css";
import "swiper/css/zoom";
import { cn } from "../../../../utils/cn.ts";
import BareResponsiveImage from "../../../../components/responsive-image/bare-responsive-image.tsx";
import { ArrowButton } from "../../../../components/buttons/arrow-button.tsx";
import { usePropertyDetailsContext } from "../../../contexts/property-details-context.tsx";
import { CloseButton } from "../../../../components/buttons/close-button.tsx";

interface PhotosModalProps {
  initialIndex: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  focusOnClose: () => HTMLElement | null;
}

export const PhotosModal = ({ initialIndex, isOpen, onOpenChange, focusOnClose }: PhotosModalProps) => {
  const { images = [] } = usePropertyDetailsContext();
  const swiperRef = useRef<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  const handleWheelZoom = (e: WheelEvent, swiper: SwiperType) => {
    e.preventDefault();
    const zoom = swiper.zoom;
    const scaleStep = 0.1;
    if (e.deltaY < 0) {
      zoom.in(zoom.scale + scaleStep);
    } else {
      zoom.out();
    }
  };
  if (images.length === 0) return null;

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="twp fixed inset-0 z-[10000] bg-black/90 data-[state=open]:animate-fade-in-grow data-[state=closed]:animate-fade-out-shrink" />
        <DialogPrimitive.Content
          className={cn(
            "twp",
            "fixed inset-0 z-[10001] flex flex-col",
            "focus:outline-none",
            isOpen ? "animate-fade-in-grow" : "animate-fade-out-shrink",
          )}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            focusOnClose()?.focus();
          }}
        >
          <DialogPrimitive.Title className="sr-only">Image viewer</DialogPrimitive.Title>
          <DialogPrimitive.Close asChild>
            <CloseButton
              aria-label="Close image viewer"
              variant="onDarkNeutral"
              size="xl"
              className="absolute z-10 size-7.5 p-0 top-4.5 right-4 md:top-8 md:right-8 lg:top-12 lg:right-12"
            />
          </DialogPrimitive.Close>

          {/* Swiper */}
          <div className="flex-1 min-h-0 relative flex items-center">
            <Swiper
              slidesPerView={1}
              spaceBetween={16}
              zoom={true}
              keyboard={{
                enabled: true,
                onlyInViewport: true, // optional
              }}
              loop={images.length > 1}
              initialSlide={initialIndex}
              className="w-full h-full"
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                const container = swiper.el;
                container.addEventListener("wheel", (e) => handleWheelZoom(e, swiper), {
                  passive: false,
                });
              }}
              onSlideChange={(swiper) => {
                setActiveIndex(swiper.realIndex);
              }}
              modules={[Zoom, Keyboard]}
            >
              {images.map((image, index) => (
                <SwiperSlide key={index}>
                  <div className="swiper-zoom-container px-4 py-14 md:px-8 md:py-24 lg:p-[130px]">
                    <BareResponsiveImage image={image} className={"w-full !max-w-[1300px] max-h-[calc(100vh-206px)]"} />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {images.length > 1 && (
              <div className="absolute hidden max-w-[1552px] m-auto lg:flex inset-0 items-center justify-between px-12   pointer-events-none">
                <ArrowButton
                  direction="prev"
                  ariaLabel="Previous image"
                  onClick={() => swiperRef.current?.slidePrev()}
                />
                <ArrowButton direction="next" ariaLabel="Next image" onClick={() => swiperRef.current?.slideNext()} />
              </div>
            )}
          </div>

          <p
            className={cn(
              "absolute  left-1/2 -translate-x-1/2 z-10  text-neutral-white   tabular-nums pointer-events-none",
              "bottom-4.5 md:bottom-8 lg:bottom-12",
              "text-body-xl md:text-small-h4",
              "!leading-none",
            )}
          >
            {activeIndex + 1} / {images.length}
          </p>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
